// src/views/leads/uploadCsvFile.tsx - Following EXACT pattern from BulkProductImport

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-hot-toast";
import styled from 'styled-components';
import Papa from 'papaparse';

// Following the exact import pattern from BulkProductImport
import PopupComponent from "../../components/PopupComponent";
import GenericConfirmationModal from "../../components/GenericConfirmationModal";
import DragDropCSV from './leadDetails/DragDropCSV';

// Import our utilities following the same pattern
import { 
  parseCSV, 
  generateCSVTemplate, 
  generateAutoMappings, 
  validateLeadData, 
  storeMappingForFuture, 
  validateMapping,
  LeadFieldDefinition 
} from "../../utils/csvUtilities";

// Following exact same interface pattern as BulkProductImport
import { setLeads, setLeadName } from "../../actions/actions";
import { createNewLeadByCsv, downloadCsv, getAllLeads } from "../../services/leadService";

// Icons following the same pattern
import { FaFileUpload, FaCheckCircle, FaSpinner, FaInfoCircle } from "react-icons/fa";
import { ArrowRightLeft, Check, HelpCircle, Shield, Trophy, Upload } from "lucide-react";
import UploadSheetFile from '../../UploadSheetFile';
import ImportGoogleContacts from './ImportGoogleContacts';
import logger from '../../utils/logger';

// Styled components following BulkProductImport pattern
const StepContent = styled.div`
  margin-top: 24px;
  margin-bottom: 24px;
`;

const UploadArea = styled.div`
  border: 2px dashed #d9d9d9;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  background: #fafafa;
  cursor: pointer;
  transition: border-color 0.3s;
  margin-bottom: 20px;

  &:hover {
    border-color: #1890ff;
  }
`;

const StepsContainer = styled.div`
  margin-bottom: 24px;
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: space-between;
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px 24px;
  background-color: white;
  border-top: 1px solid #f0f0f0;
  z-index: 10;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.05);
`;

const CompletionContent = styled.div`
  text-align: center;
  padding: 30px 20px;

  .success-icon {
    font-size: 48px;
    color: #52c41a;
    margin-bottom: 16px;
  }
`;

const StepIndicator = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 32px;
`;

const Step = styled.div<{ active: boolean; completed: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  position: relative;

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    top: 20px;
    left: 60%;
    width: 80%;
    height: 2px;
    background-color: ${props => props.completed ? '#52c41a' : '#d9d9d9'};
  }

  .step-number {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    font-size: 16px;
    background-color: ${props => 
      props.completed ? '#52c41a' : 
      props.active ? '#1890ff' : '#d9d9d9'
    };
    color: ${props => (props.completed || props.active) ? 'white' : '#666'};
    margin-bottom: 8px;
    z-index: 1;
    position: relative;
  }

  .step-title {
    font-size: 14px;
    font-weight: 500;
    color: ${props => props.active ? '#1890ff' : '#666'};
    text-align: center;
  }
`;

const ValidationResults = styled.div`
  .validation-summary {
    background: #f6ffed;
    border: 1px solid #b7eb8f;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 24px;
  }

  .field-validation {
    border: 1px solid #d9d9d9;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 16px;

    &.has-issues {
      border-color: #ff7875;
      background: #fff2f0;
    }

    &.valid {
      border-color: #52c41a;
      background: #f6ffed;
    }
  }

  .validation-stats {
    display: flex;
    gap: 16px;
    margin-bottom: 12px;

    .valid {
      color: #52c41a;
      font-weight: 500;
    }

    .invalid {
      color: #ff4d4f;
      font-weight: 500;
    }
  }

  .recommendations {
    margin-top: 12px;
    
    h5 {
      margin-bottom: 8px;
      color: #1890ff;
    }

    ul {
      margin: 0;
      padding-left: 20px;
    }

    li {
      margin-bottom: 4px;
      color: #666;
    }
  }

  .samples {
    margin-top: 12px;
    font-size: 12px;
    
    .sample-label {
      font-weight: 500;
      margin-bottom: 4px;
    }
    
    .sample-values {
      color: #666;
      font-family: monospace;
    }
  }
`;

// Mapping component following the existing pattern
const MappingItem = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: 1rem;
  align-items: center;
  padding: 0.5rem 1rem;
  margin-bottom: 12px;
  background: white;
  border-radius: 8px;
  border: 1px solid #e8e8e8;
`;

const MappingLabel = styled.label`
  font-weight: 500;
  color: #374151;
  
  .required {
    color: #ff4d4f;
  }
`;

const MappingSelect = styled.select`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #E5E7EB;
  border-radius: 0.375rem;
  background-color: white;
  color: #1F2937;
  
  &:focus {
    outline: none;
    border-color: #4A90E2;
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2);
  }
`;

const HelpLinkSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  
  a {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #3a4b86;
    text-decoration: none;
    padding: 8px 16px;
    border-radius: 8px;
    background: #f0f2f5;
    transition: all 0.2s ease;
    
    &:hover {
      background: #e4e7ed;
      text-decoration: none;
    }
  }
`;

interface Props {
  selectedList: {
    id: string;
    name: string;
  };
}

const UploadCsvFile: React.FC<Props> = ({ selectedList }) => {
  // Following EXACT state pattern from BulkProductImport
  const [visible, setVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvData, setCsvData] = useState<{ headers: string[]; data: any[] }>({ headers: [], data: [] });
  const [fieldMapping, setFieldMapping] = useState<Record<string, string>>({});
const [isUploading, setIsUploading] = useState(false);
const [isReadingFile, setIsReadingFile] = useState(false);
const [error, setError] = useState<string | null>(null);

  const [formCloseModel, setFormCloseModel] = useState(false);
  const [validationResult, setValidationResult] = useState<any>(null);
  const [showExtraDetails, setShowExtraDetails] = useState(false);
  
  // Reference to track if fields are already initialized (following BulkProductImport pattern)
  const fieldsInitializedRef = useRef(false);

  // Following the exact Redux pattern from uploadCsvFile
  const StoreData = useSelector((state: any) => state?.rootReducers);
  const dispatch = useDispatch();

  // Get lead field definitions from Redux store (following existing pattern)
  const [leadFields, setLeadFields] = useState<LeadFieldDefinition[]>([]);

  // Effect to set up lead fields (following BulkProductImport pattern)
  useEffect(() => {
    if (fieldsInitializedRef.current) return;
    
    logger.log('🔧 Initializing lead fields from user preferences');
    fieldsInitializedRef.current = true;
    
    const customFields: LeadFieldDefinition[] = StoreData.user?.userPreferences?.customForm || [];
    logger.log('📋 Available custom fields:', customFields);
    
    setLeadFields(customFields);
    
    // Initialize field mapping object
    const initialMapping = customFields.reduce((acc: Record<string, string>, field: LeadFieldDefinition) => {
      acc[field.name] = ""; // Using field.name as the key for UI display
      return acc;
    }, {} as Record<string, string>);
    setFieldMapping(initialMapping);
    
  }, [StoreData.user?.userPreferences?.customForm]);

  // Reset states when popup is closed (following BulkProductImport pattern)
  useEffect(() => {
    if (!visible) {
      logger.log('🔄 Resetting component state');
      setCurrentStep(0);
      setCsvFile(null);
      setCsvData({ headers: [], data: [] });
      setFieldMapping({});
      setIsUploading(false);
      setError(null);
      setValidationResult(null);
      fieldsInitializedRef.current = false;
    }
  }, [visible]);

  // Generate template CSV (following BulkProductImport pattern)
const generateTemplate = useCallback(async () => {
  logger.log('📥 Downloading CSV template from server...');
  
  try {
    const response = await downloadCsv("");
    
    if (response && response.status) {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "lead-import-template.csv"); // Just changed filename
      document.body.appendChild(link);
      link.click();
      toast.success("Template downloaded successfully");
      logger.log('✅ Template downloaded successfully');
    }
  } catch (err) {
    console.error('❌ Error downloading template:', err);
    toast.error("Error while downloading CSV template!");
  }
}, []);


  // Add file validation before parsing
const validateFileBeforeParsing = (file: File): { isValid: boolean; error?: string } => {
  logger.log('🔍 Validating file before parsing:', file.name);
  
  // Check file extension
  if (!file.name.toLowerCase().endsWith('.csv')) {
    return { isValid: false, error: 'Please upload a CSV file' };
  }
  
  // Check MIME type
  const allowedMimeTypes = ['text/csv', 'application/csv', 'text/plain'];
  if (file.type && !allowedMimeTypes.includes(file.type)) {
    return { isValid: false, error: 'Invalid file type. Only CSV files are allowed.' };
  }
  
  // Check file size BEFORE parsing (50MB limit)
  if (file.size > 50 * 1024 * 1024) {
    return { isValid: false, error: 'File size should be less than 50MB' };
  }
  
  // Check if file is empty
  if (file.size === 0) {
    return { isValid: false, error: 'File appears to be empty' };
  }
  
  // Check minimum size
  if (file.size < 10) {
    return { isValid: false, error: 'File is too small to contain valid CSV data' };
  }
  
  return { isValid: true };
};

  // Handle file upload (following EXACT BulkProductImport pattern)

const handleFileUpload = async (file: File) => {
  try {
    logger.log('📁 Starting file upload process:', file.name);
    
    // Validate file BEFORE parsing
    const validation = validateFileBeforeParsing(file);
    if (!validation.isValid) {
      setError(validation.error!);
      return false;
    }
    
    // Set loading state for file reading
    setIsReadingFile(true);
    setError(null);
    logger.log('📖 Starting to read and parse CSV file...');

    const result = await parseCSV(file);
    logger.log('✅ CSV parsed successfully:', result);
    
    setCsvFile(file);
    setCsvData(result);
    
    if (result.headers.length > 0) {
      // Auto-generate mappings following the exact pattern
      logger.log('🤖 Generating auto-mappings');
      const storedMappings = {}; // TODO: Get from Redux later
      const autoMappings = generateAutoMappings(result.headers, leadFields, storedMappings);
      
      logger.log('🎯 Auto-mappings generated:', autoMappings);
      setFieldMapping(prev => ({
        ...prev,
        ...autoMappings
      }));
      
      if (Object.keys(autoMappings).length > 0) {
        toast.success(`Auto-mapped ${Object.keys(autoMappings).length} fields`);
      }
      
      setCurrentStep(1); // Move to mapping step
      setError(null);
    } else {
      setError('The CSV file has no headers. Please check the file format.');
    }
    
    return false; // Prevent default upload behavior
  } catch (error: any) {
    console.error('❌ CSV parsing error:', error);
    setError('Failed to parse CSV file. Please check the format.');
    return false;
  } finally {
    // Always clear loading state
    setIsReadingFile(false);
  }
};

 // Format field name for display (remove underscores and capitalize)
 const formatFieldNameForDisplay = (fieldName: string): string => {
   // If it's an extra_details field, format it nicely
   if (fieldName.startsWith('extra_details_')) {
     const withoutPrefix = fieldName.replace('extra_details_', '');
     return withoutPrefix
       .split('_')
       .map(word => word.charAt(0).toUpperCase() + word.slice(1))
       .join(' ');
   }
   
   // For regular fields, try to find the display label
   const leadField = leadFields.find(f => f.name === fieldName);
   if (leadField) {
     return leadField.label || leadField.name;
   }
   
   // Fallback: just capitalize and remove underscores
   return fieldName
     .split('_')
     .map(word => word.charAt(0).toUpperCase() + word.slice(1))
     .join(' ');
 };

 // Handle field mapping changes (following existing pattern)
 const handleMappingChange = (fieldName: string, csvColumn: string) => {
   logger.log('🔄 Mapping change:', fieldName, '->', csvColumn);
   setFieldMapping(prev => ({
     ...prev,
     [fieldName]: csvColumn
   }));
   setError(null); // Clear any previous mapping errors
 };

 // Validate mappings before proceeding (following BulkProductImport pattern)
const validateBeforeProceeding = () => {
  logger.log('✅ Validating mappings before proceeding');
  
  // Only check the 2 fields that API actually requires
  const phoneField = leadFields.find(f => f.value === 'phone' || f.name.toLowerCase().includes('phone'));
  const nameField = leadFields.find(f => f.value === 'name' || f.name.toLowerCase().includes('name'));
  
  // Check phone mapping
  if (phoneField && (!fieldMapping[phoneField.name] || fieldMapping[phoneField.name] === "" || fieldMapping[phoneField.name] === "Don't import")) {
    setError("Phone Number field is required and must be mapped");
    return false;
  }
  
  // Check name mapping
  if (nameField && (!fieldMapping[nameField.name] || fieldMapping[nameField.name] === "" || fieldMapping[nameField.name] === "Don't import")) {
    setError("Lead Name field is required and must be mapped");
    return false;
  }
  
  // Check if at least one field is mapped
  const mappedFields = Object.values(fieldMapping).filter(v => v !== "" && v !== "Don't import");
  if (mappedFields.length === 0) {
    setError('Please map at least one field');
    return false;
  }
  
  return true;
};

 // Get system data for validation
 const getSystemDataForValidation = () => {
   const systemData: any = {};
   
   try {
     // Get status labels from localStorage (same as status.tsx)
     const preferences = localStorage.getItem("user_preferences");
     if (preferences) {
       const parsedPreferences = JSON.parse(preferences);
       if (parsedPreferences?.status) {
         systemData.statuses = parsedPreferences.status.map((item: any) => item.name || item.value);
         logger.log('📊 Found system statuses:', systemData.statuses);
       }
       if (parsedPreferences?.source) {
         systemData.sources = parsedPreferences.source.map((item: any) => item.name || item.value);
         logger.log('📊 Found system sources:', systemData.sources);
       }
     }
   } catch (error) {
     logger.log('⚠️ Error loading user preferences:', error);
   }
   
   // Get team members from Redux store
   if (StoreData.user?.organizationEmployee) {
     systemData.teamMembers = StoreData.user.organizationEmployee;
     logger.log('📊 Found team members:', systemData.teamMembers.length);
   }
   
   return systemData;
 };

 // Validate data and show results (NEW step following your requirements)
 const validateAndShowResults = () => {
   if (!validateBeforeProceeding()) {
     return;
   }

   logger.log('🔍 Starting data validation');
   setError(null);
   
   try {
     // Get system data for validation
     const systemData = getSystemDataForValidation();
     
     const validation = validateLeadData(csvData.data, fieldMapping, leadFields, systemData);
     logger.log('📊 Validation results:', validation);
     
     setValidationResult(validation);
     setCurrentStep(2); // Move to validation step
     
     if (!validation.canProceed) {
       
     } else {
       toast.success('Data validation completed successfully');
     }
   } catch (error) {
     console.error('❌ Validation error:', error);
     setError('Failed to validate data. Please try again.');
   }
 };

 // Start the import process (following BulkProductImport pattern exactly)
 const startImport = async () => {
   if (!csvFile || !validateBeforeProceeding()) {
     return;
   }

   setIsUploading(true);
   setError(null);

   try {
     logger.log('🚀 Starting import process');
     
     // Store successful mappings for future use (following your requirements)
     const mappingsToStore = storeMappingForFuture(fieldMapping, leadFields);
     logger.log('💾 Mappings to store for future:', mappingsToStore);
     // TODO: dispatch(storeCsvMappings(mappingsToStore)); // Redux action

     // Prepare the upload following the EXACT existing pattern
     const form_Data = StoreData.user?.userPreferences?.customForm;
     const keyMapping = form_Data.reduce((acc: any, obj: any) => {
       acc[obj.name] = obj.value;
       return acc;
     }, {});

     // Create updated object with keys replaced using the keyMapping (EXACT existing logic)
     const updatedObject = Object.keys(fieldMapping).reduce((acc: any, key) => {
       const mappedValue = fieldMapping[key];
       if (mappedValue && mappedValue !== "Don't import") {
         acc[keyMapping[key] || key] = mappedValue;
       }
       return acc;
     }, {});

     logger.log('🔄 Field mappings for API:', updatedObject);

     // Validation following EXACT existing logic
     if (!updatedObject.phone || updatedObject.phone.length < 1) {
       setError("Please map the Phone Number field");
       setIsUploading(false);
       return;
     }
     if (!updatedObject.name || updatedObject.name.length < 2) {
       setError("Please map the Lead Name field");
       setIsUploading(false);
       return;
     }

     // Prepare FormData following EXACT existing pattern
     const formData = new FormData();
     formData.append("csv", csvFile);

     // Add mappings to form data (EXACT existing logic)
     Object.keys(updatedObject).forEach(key => {
       if (updatedObject[key] && updatedObject[key] !== "Don't import") {
         formData.append(key, updatedObject[key]);
       }
     });

     // Add list ID if not default (EXACT existing logic)
     if (selectedList.id !== "0") {
       formData.append("list", selectedList.id);
     }

    const response = await createNewLeadByCsv(formData);
const responseData = response.data;
     logger.log('✅ Import successful:', responseData);

   

     // Refresh leads data following existing pattern
     try {
       const leadsResponse = await getAllLeads({
         isAscending: false,
         page: 1,
         perPage: 10,
       });
       if (leadsResponse && leadsResponse.status) {
         dispatch(setLeads(leadsResponse.data.data));
       }
     } catch (err) {
       logger.log('Warning: Failed to refresh leads data');
     }

     // Move to success step
     setCurrentStep(3);
     toast.success('Leads imported successfully!');

   } catch (error: any) {
     console.error('❌ Import error:', error);
     setError(error.message || 'Failed to import leads. Please try again.');
   } finally {
     setIsUploading(false);
   }
 };

 // Handle next step button (following BulkProductImport pattern)
 const handleNext = () => {
   logger.log('➡️ Moving to next step from:', currentStep);
   
   if (currentStep === 0 && !csvFile) {
     setError('Please upload a CSV file first.');
     return;
   }

   if (currentStep === 1) {
     validateAndShowResults(); // Move to validation step
     return;
   }

   if (currentStep === 2) {
     startImport(); // Start the actual import
     return;
   }

   setCurrentStep(prev => prev + 1);
 };

 // Handle previous step button (following BulkProductImport pattern)
 const handlePrevious = () => {
   logger.log('⬅️ Moving to previous step from:', currentStep);
   setCurrentStep(prev => prev - 1);
   setError(null);
 };

 // Handle drawer close with confirmation (following BulkProductImport pattern)
const handleClosePopup = () => {
  // Don't show warning if import is complete (step 3) or no file uploaded
  if (currentStep === 3 || !csvFile || currentStep === 0 || isUploading) {
    setVisible(false);
  } else {
    // Only show warning if user has progress but hasn't finished
    setFormCloseModel(true);
  }
};

 // Can proceed to step validation (following BulkProductImport pattern)
 const canProceedToStep = (step: number): boolean => {
   switch(step) {
     case 1: return csvFile !== null && csvData.headers.length > 0;
     case 2: return Object.values(fieldMapping).some(v => v !== "" && v !== "Don't import");
     case 3: return validationResult !== null;
     default: return true;
   }
 };

 // Render different content based on current step (following BulkProductImport pattern)
 const renderStepContent = () => {
   switch (currentStep) {
     case 0: // Upload file
       return (
         <StepContent>
           <div style={{ textAlign: 'center', marginBottom: '24px' }}>
             <h3>Bulk import leads 📥</h3>
             <p style={{ fontSize: '16px', color: '#666', marginBottom: '16px' }}>
               Streamline your lead management process by importing your leads in bulk.
             </p>
           </div>

           <HelpLinkSection>
             <a 
               href="https://help.3sigmacrm.com/welcome/adding-or-importing-leads/bulk-import-leads-csv" 
               target="_blank"
               rel="noopener noreferrer"
             >
               <HelpCircle size={16} />
               <span>Need help with CSV import?</span>
             </a>
           </HelpLinkSection>
           
         <UploadArea onClick={() => !isReadingFile && document.getElementById('csv-upload-input')?.click()}>
  <input
    id="csv-upload-input"
    type="file"
    accept=".csv"
    style={{ display: 'none' }}
    disabled={isReadingFile}
    onChange={(e) => {
      const file = e.target.files?.[0];
      if (file && !isReadingFile) {
        handleFileUpload(file);
      }
    }}
  />
  
  {isReadingFile ? (
    <>
      <FaSpinner 
        style={{ 
          fontSize: 48, 
          color: '#1890ff', 
          marginBottom: 16,
          animation: 'spin 1s linear infinite' 
        }} 
      />
      <p style={{ marginBottom: '16px', color: '#1890ff', fontWeight: '600' }}>
        Reading and analyzing your CSV file...
      </p>
      <p style={{ color: '#666', fontSize: '14px' }}>
        Please wait while we process your file
      </p>
    </>
  ) : (
    <>
      <FaFileUpload style={{ fontSize: 48, color: '#1890ff', marginBottom: 16 }} />
      <p style={{ marginBottom: '16px' }}>
        Click or drag a CSV file to this area to upload
      </p>
      <button 
        type="button"
        style={{
          background: '#1890ff',
          color: 'white',
          border: 'none',
          padding: '8px 16px',
          borderRadius: '4px',
          cursor: 'pointer'
        }}
      >
        Select File
      </button>
    </>
  )}
</UploadArea>
           
           <div style={{ textAlign: 'center', marginTop: '16px' }}>
  {isReadingFile ? (
    <div style={{ color: '#1890ff', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
      <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
      <span>Processing file...</span>
    </div>
  ) : (
    <>
      <FaInfoCircle style={{ marginRight: '8px', color: '#1890ff' }} />
      <span style={{ color: '#666' }}>Need a template? </span>
      <button 
        type="button"
        onClick={generateTemplate}
        style={{
          background: 'none',
          border: 'none',
          color: '#1890ff',
          textDecoration: 'underline',
          cursor: 'pointer'
        }}
      >
        Download CSV template
      </button>
    </>
  )}
</div>
           
           {csvFile && (
             <div style={{ 
               background: '#f6ffed', 
               border: '1px solid #b7eb8f', 
               borderRadius: '8px', 
               padding: '16px', 
               marginTop: '16px',
               display: 'flex',
               alignItems: 'center'
             }}>
               <FaCheckCircle style={{ color: '#52c41a', marginRight: '8px' }} />
               <div>
                 <strong>File Selected</strong>
                 <br />
                 <span style={{ color: '#666' }}>
                   {csvFile.name} ({(csvFile.size / 1024).toFixed(2)} KB, {csvData.data.length} rows)
                 </span>
               </div>
             </div>
           )}
         </StepContent>
       );
     
     case 1: // Map fields
       return (
         <StepContent>
           <div style={{ textAlign: 'center', marginBottom: '24px' }}>
             <h3>Map CSV Fields 🗺️</h3>
             <p style={{ color: '#666' }}>
               Map the columns from your CSV file to the corresponding lead fields. 
               Required fields are marked with an asterisk (*).
             </p>
           </div>
           
           <div style={{ marginBottom: '24px' }}>
             <div style={{ 
               display: 'flex', 
               justifyContent: 'space-between',
               padding: '0 16px',
               fontWeight: 'bold',
               color: '#666',
               marginBottom: '16px'
             }}>
               <span>CRM Fields</span>
               <span>CSV Columns</span>
             </div>
             
             {leadFields.map((field, index) => {
               const isRequired = field.required || field.isRequired;
               return (
                 <MappingItem key={index}>
                   <MappingLabel>
                     {field.label || field.name}
                     {isRequired && <span className="required"> *</span>}
                   </MappingLabel>
                   <MappingSelect
                     value={fieldMapping[field.name] || ""}
                     onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleMappingChange(field.name, e.target.value)}
                   >
                     <option value="">Select column</option>
                     {csvData.headers.map((header, idx) => {
                       // Check if this option is already selected by another field
                       const isAlreadySelected = Object.entries(fieldMapping).some(
                         ([otherField, selectedHeader]) => 
                           otherField !== field.name && 
                           selectedHeader === header && 
                           header !== "Don't import"
                       );
                       
                       return (
                         <option 
                           key={idx} 
                           value={header}
                           disabled={isAlreadySelected}
                           style={isAlreadySelected ? { color: '#ccc' } : {}}
                         >
                           {header} {isAlreadySelected ? '(already used)' : ''}
                         </option>
                       );
                     })}
                     <option value="Don't import">Don't import</option>
                   </MappingSelect>
                 </MappingItem>
               );
             })}
           </div>

           {/* Extra Details Section - Collapsible */}
           {csvData.headers.length > 0 && (() => {
             // Get headers not used in LEAD FIELD mapping (not including extra details)
             const leadFieldHeaders = Object.entries(fieldMapping)
               .filter(([key, val]) => !key.startsWith('extra_details_') && val && val !== "Don't import")
               .map(([key, val]) => val);
             
             // System fields that are auto-handled by backend - exclude from extra details
             const systemFields = [
               'status label', 'status', 'label', 'labels', 'lead status', 'lead label',
               'source', 'sources', 'lead source', 'lead sources',
               'assign to', 'assignto', 'assigned to', 'assignedto', 'assign', 'assigned',
               'list', 'lead list', 'leadlist', 'list name'
             ];
             
             // Debug: log CSV headers and system fields
             logger.log(`🔍 CSV Headers:`, csvData.headers);
             logger.log(`🔍 System Fields to Block:`, systemFields);
             logger.log(`🔍 Lead Field Headers (already mapped):`, leadFieldHeaders);
             
             const availableForExtraDetails = csvData.headers.filter(header => {
               // Skip if already mapped to lead fields
               if (leadFieldHeaders.includes(header)) {
                 logger.log(`🚫 Skipping "${header}" - already mapped to lead field`);
                 return false;
               }
               
               // Skip if it's a system field (case-insensitive, trimmed)
               const normalizedHeader = header.trim().toLowerCase();
               
               // Debug: log what we're checking
               logger.log(`🔍 Checking header: "${header}" (normalized: "${normalizedHeader}")`);
               logger.log(`🔍 System fields list:`, systemFields);
               
               const isSystemField = systemFields.some(sysField => {
                 const normalizedSysField = sysField.toLowerCase();
                 const matches = normalizedHeader === normalizedSysField;
                 logger.log(`  🔗 "${normalizedHeader}" === "${normalizedSysField}" ? ${matches}`);
                 return matches;
               });
               
               if (isSystemField) {
                 logger.log(`🚫 BLOCKED: "${header}" is a system field (normalized: "${normalizedHeader}")`);
                 return false;
               }
               
               logger.log(`✅ Available for extra details: "${header}"`);
               return true;
             });
             
             if (availableForExtraDetails.length === 0) return null;
             
             // Count selected extra details
             const selectedCount = availableForExtraDetails.filter(header => {
               const extraDetailKey = `extra_details_${header.toLowerCase().replace(/[^a-z0-9]/g, '_')}`;
               return fieldMapping[extraDetailKey] === header;
             }).length;
             
             return (
               <div style={{ marginTop: '32px' }}>
                 <div style={{ 
                   borderTop: '2px solid #f0f0f0', 
                   paddingTop: '24px',
                   marginBottom: '16px'
                 }}>
                   <button
                     type="button"
                     onClick={() => setShowExtraDetails(!showExtraDetails)}
                     style={{
                       width: '100%',
                       display: 'flex',
                       alignItems: 'center',
                       justifyContent: 'space-between',
                       background: 'none',
                       border: 'none',
                       cursor: 'pointer',
                       padding: '8px 0'
                     }}
                   >
                     <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                       <h4 style={{ 
                         margin: 0, 
                         color: '#1890ff',
                         fontSize: '16px'
                       }}>
                         🔧 Extra Details
                       </h4>
                       <span style={{ 
                         fontSize: '12px', 
                         color: '#666', 
                         fontWeight: 'normal' 
                       }}>
                         ({availableForExtraDetails.length} available
                         {selectedCount > 0 && `, ${selectedCount} selected`})
                       </span>
                     </div>
                     <span style={{ 
                       fontSize: '18px', 
                       color: '#1890ff',
                       transform: showExtraDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                       transition: 'transform 0.2s ease'
                     }}>
                       ▼
                     </span>
                   </button>
                   
                   {!showExtraDetails && (
                     <p style={{ 
                       color: '#666', 
                       fontSize: '13px', 
                       margin: '4px 0 0 0',
                       fontStyle: 'italic'
                     }}>
                       Click to expand optional extra details mapping
                     </p>
                   )}
                 </div>

                 {showExtraDetails && (
                   <>
                     <p style={{ 
                       color: '#666', 
                       fontSize: '13px', 
                       margin: '0 0 16px 0' 
                     }}>
                       Import additional CSV columns as custom extra details. These will be stored with "extra_details_" prefix.
                     </p>

                     <div style={{ 
                       display: 'flex', 
                       justifyContent: 'space-between',
                       padding: '0 16px',
                       fontWeight: 'bold',
                       color: '#666',
                       marginBottom: '16px'
                     }}>
                       <span>CSV Columns (Unmapped)</span>
                       <span>Import as Extra Detail</span>
                     </div>

                 {availableForExtraDetails.map((header, idx) => {
                   const extraDetailKey = `extra_details_${header.toLowerCase().replace(/[^a-z0-9]/g, '_')}`;
                   const isSelected = fieldMapping[extraDetailKey] === header;
                   
                   return (
                     <MappingItem key={`extra_${idx}`} style={{
                       backgroundColor: isSelected ? '#f6ffed' : 'white',
                       border: isSelected ? '1px solid #b7eb8f' : '1px solid #e8e8e8'
                     }}>
                       <MappingLabel>
                         <span style={{ color: isSelected ? '#52c41a' : '#1890ff' }}>
                           {isSelected ? '✅' : '📋'}
                         </span> {header}
                         <div style={{ 
                           fontSize: '11px', 
                           color: isSelected ? '#52c41a' : '#999', 
                           fontWeight: 'normal',
                           marginTop: '2px'
                         }}>
                           Key: {extraDetailKey}
                           {isSelected && <span style={{ marginLeft: '8px', color: '#52c41a' }}>● Selected</span>}
                         </div>
                       </MappingLabel>
                       <MappingSelect
                         value={fieldMapping[extraDetailKey] || ""}
                         onChange={(e: React.ChangeEvent<HTMLSelectElement>) => 
                           handleMappingChange(extraDetailKey, e.target.value)
                         }
                         style={{
                           borderColor: isSelected ? '#52c41a' : '#E5E7EB'
                         }}
                       >
                         <option value="">Don't import as extra detail</option>
                         <option value={header}>Import as extra detail</option>
                       </MappingSelect>
                     </MappingItem>
                   );
                 })}
                   </>
                 )}
               </div>
             );
           })()}

           {/* Preview data sample */}
           {csvData.data.length > 0 && (
             <div style={{ marginTop: '24px' }}>
               <h4>Data Preview</h4>
               <div style={{ 
                 overflowX: 'auto', 
                 maxHeight: '200px', 
                 border: '1px solid #e8e8e8',
                 borderRadius: '8px'
               }}>
                 <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                   <thead style={{ position: 'sticky', top: 0, background: '#fafafa' }}>
                     <tr>
                       {csvData.headers.slice(0, 5).map(header => (
                         <th key={header} style={{ 
                           border: '1px solid #f0f0f0', 
                           padding: '8px', 
                           textAlign: 'left'
                         }}>
                           {header}
                         </th>
                       ))}
                       {csvData.headers.length > 5 && (
                         <th style={{ border: '1px solid #f0f0f0', padding: '8px' }}>
                           +{csvData.headers.length - 5} more
                         </th>
                       )}
                     </tr>
                   </thead>
                   <tbody>
                     {csvData.data.slice(0, 3).map((row, index) => (
                       <tr key={index}>
                         {csvData.headers.slice(0, 5).map(header => (
                           <td key={`${index}-${header}`} style={{ 
                             border: '1px solid #f0f0f0', 
                             padding: '8px',
                             maxWidth: '150px',
                             overflow: 'hidden',
                             textOverflow: 'ellipsis',
                             whiteSpace: 'nowrap'
                           }}>
                             {row[header]}
                           </td>
                         ))}
                         {csvData.headers.length > 5 && (
                           <td style={{ border: '1px solid #f0f0f0', padding: '8px', color: '#666' }}>
                             ...
                           </td>
                         )}
                       </tr>
                     ))}
                   </tbody>
                 </table>
               </div>
               {csvData.data.length > 3 && (
                 <p style={{ color: '#666', fontSize: '12px', marginTop: '8px' }}>
                   Showing 3 of {csvData.data.length} rows
                 </p>
               )}
             </div>
           )}
         </StepContent>
       );
     
   case 2: // Step 3 - Simple Validation Results
  return (
    <div style={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {/* Simple Header */}
      <div style={{ 
        textAlign: 'center', 
        marginBottom: '24px',
        flexShrink: 0
      }}>
        <h3 style={{ 
          margin: '0 0 8px 0',
          fontSize: '20px',
          fontWeight: '600',
          color: '#1e293b'
        }}>
          Data Validation 🔍
        </h3>
        <p style={{ 
          margin: 0,
          color: '#64748b',
          fontSize: '14px'
        }}>
          Checking {validationResult?.sampleSize || 0} rows for issues
        </p>
      </div>

      {validationResult && (
        <div style={{ 
          flex: 1,
          overflow: 'auto'
        }}>
          {/* Simple Status Summary */}
          <div style={{
            background: validationResult.canProceed ? '#f0fdf4' : '#fef2f2',
            border: `2px solid ${validationResult.canProceed ? '#22c55e' : '#ef4444'}`,
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '24px',
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '24px',
              marginBottom: '8px'
            }}>
              {validationResult.canProceed ? '✅' : '⚠️'}
            </div>
            <div style={{
              fontSize: '16px',
              fontWeight: '600',
              color: validationResult.canProceed ? '#16a34a' : '#dc2626',
              marginBottom: '4px'
            }}>
              {validationResult.canProceed ? 'Data Looks Good!' : 'Issues Found'}
            </div>
            <div style={{
              fontSize: '14px',
              color: '#666'
            }}>
              {validationResult.totalRows} rows total • {validationResult.sampleSize} analyzed
            </div>
          </div>

          {/* Compact Card Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '16px'
          }}>
            {Object.entries(validationResult.fieldResults).map(([field, validation]: [string, any]) => {
              const hasIssues = validation.invalidCount > 0;
              const totalChecked = validation.validCount + validation.invalidCount;
              const validPercentage = totalChecked > 0 ? Math.round((validation.validCount / totalChecked) * 100) : 0;
              const isSystemField = ['status', 'source', 'assign', 'list'].some(sys => 
                field.toLowerCase().includes(sys)
              );
              
              return (
                <div 
                  key={field}
                  style={{
                    background: 'white',
                    border: `2px solid ${hasIssues ? '#f59e0b' : '#22c55e'}`,
                    borderRadius: '12px',
                    padding: '16px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.2s ease',
                    minHeight: '120px',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  {/* Header */}
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    marginBottom: '12px'
                  }}>
                    <div style={{
                      fontSize: '24px'
                    }}>
                      {hasIssues ? '⚠️' : '✅'}
                    </div>
                    <div style={{
                      fontSize: '20px',
                      fontWeight: '700',
                      color: hasIssues ? '#f59e0b' : '#22c55e'
                    }}>
                      {validPercentage}%
                    </div>
                  </div>

                  {/* Field Name */}
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#1e293b',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    flexWrap: 'wrap'
                  }}>
                    {formatFieldNameForDisplay(field)}
                    {isSystemField && (
                      <span style={{
                        background: '#3b82f6',
                        color: 'white',
                        fontSize: '10px',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontWeight: '500'
                      }}>
                        SYSTEM
                      </span>
                    )}
                  </div>

                  {/* Stats */}
                  <div style={{
                    fontSize: '14px',
                    color: '#666',
                    marginBottom: '8px'
                  }}>
                    {validation.validCount} valid • {validation.invalidCount} issues
                  </div>

                  {/* Issue Description */}
                  {hasIssues && validation.issues && validation.issues.length > 0 && (
                    <div style={{
                      fontSize: '12px',
                      color: '#dc2626',
                      backgroundColor: '#fef2f2',
                      padding: '8px',
                      borderRadius: '6px',
                      marginTop: 'auto'
                    }}>
                      {validation.issues[0].length > 60 ? 
                        validation.issues[0].substring(0, 60) + '...' : 
                        validation.issues[0]
                      }
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );


  return (
    <div style={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{ 
        textAlign: 'center', 
        marginBottom: '24px',
        flexShrink: 0
      }}>
        <h3 style={{ 
          margin: '0 0 8px 0',
          fontSize: '24px',
          fontWeight: '700',
          color: '#1e293b'
        }}>
          Data Validation Results 🔍
        </h3>
        <p style={{ 
          margin: 0,
          color: '#64748b',
          fontSize: '16px'
        }}>
          We've analyzed your data to identify any potential issues before import.
        </p>
      </div>

      {validationResult && (
        <div style={{ 
          flex: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Summary Section */}
          <div style={{
            background: validationResult.canProceed 
              ? 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)' 
              : 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
            border: validationResult.canProceed ? '2px solid #22c55e' : '2px solid #ef4444',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '24px',
            flexShrink: 0
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: '16px',
              marginBottom: '16px'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ 
                  fontSize: '28px', 
                  fontWeight: '700', 
                  color: '#1e293b',
                  marginBottom: '4px'
                }}>
                  {validationResult.totalRows}
                </div>
                <div style={{ fontSize: '14px', color: '#64748b', fontWeight: '500' }}>
                  Total Rows
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ 
                  fontSize: '28px', 
                  fontWeight: '700', 
                  color: '#1e293b',
                  marginBottom: '4px'
                }}>
                  {validationResult.sampleSize}
                </div>
                <div style={{ fontSize: '14px', color: '#64748b', fontWeight: '500' }}>
                  Sample Analyzed
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ 
                  fontSize: '28px', 
                  fontWeight: '700', 
                  color: validationResult.canProceed ? '#22c55e' : '#ef4444',
                  marginBottom: '4px'
                }}>
                  {validationResult.canProceed ? '✓' : '⚠️'}
                </div>
                <div style={{ 
                  fontSize: '14px', 
                  color: validationResult.canProceed ? '#22c55e' : '#ef4444',
                  fontWeight: '600'
                }}>
                  {validationResult.canProceed ? 'Ready to Import' : 'Issues Found'}
                </div>
              </div>
            </div>
            
            {!validationResult.canProceed && (
              <div style={{
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '8px',
                padding: '12px',
                textAlign: 'center'
              }}>
                <strong style={{ color: '#dc2626' }}>⚠️ Critical Issues Found</strong>
                <p style={{ margin: '8px 0 0 0', color: '#7f1d1d', fontSize: '14px' }}>
                  Your data has critical issues. You can proceed anyway (only valid rows will be imported) or go back to fix mappings.
                </p>
              </div>
            )}
          </div>

          {/* Field Results - Card Grid */}
          <div style={{
            flex: 1,
            overflow: 'auto',
            paddingRight: '8px' // Space for scrollbar
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: '16px',
              paddingBottom: '16px'
            }}>
              {Object.entries(validationResult.fieldResults).map(([field, validation]: [string, any]) => {
                const hasIssues = validation.invalidCount > 0;
                const isPhone = field.toLowerCase().includes('phone');
                
                return (
                  <div 
                    key={field}
                    style={{
                      background: 'white',
                      border: hasIssues ? '2px solid #fbbf24' : '2px solid #22c55e',
                      borderRadius: '12px',
                      padding: '20px',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {/* Field Header */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '16px'
                    }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '10px',
                        background: hasIssues 
                          ? 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)'
                          : 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '12px'
                      }}>
                        <span style={{ 
                          fontSize: '18px',
                          color: 'white'
                        }}>
                          {hasIssues ? '⚠️' : '✅'}
                        </span>
                      </div>
                      <div>
                        <h4 style={{
                          margin: 0,
                          fontSize: '18px',
                          fontWeight: '600',
                          color: '#1e293b'
                        }}>
                          {formatFieldNameForDisplay(field)}
                        </h4>
                        {isPhone && (
                          <div style={{
                            fontSize: '12px',
                            color: '#7c3aed',
                            fontWeight: '500',
                            marginTop: '2px'
                          }}>
                            📞 Phone Field - Special Validation
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Stats */}
                    <div style={{
                      display: 'flex',
                      gap: '12px',
                      marginBottom: '16px'
                    }}>
                      <div style={{
                        flex: 1,
                        background: '#f0fdf4',
                        border: '1px solid #bbf7d0',
                        borderRadius: '8px',
                        padding: '12px',
                        textAlign: 'center'
                      }}>
                        <div style={{ 
                          fontSize: '20px', 
                          fontWeight: '700', 
                          color: '#16a34a',
                          marginBottom: '4px'
                        }}>
                          {validation.validCount}
                        </div>
                        <div style={{ fontSize: '12px', color: '#166534', fontWeight: '500' }}>
                          Valid
                        </div>
                      </div>
                      {validation.invalidCount > 0 && (
                        <div style={{
                          flex: 1,
                          background: '#fef3c7',
                          border: '1px solid #fde68a',
                          borderRadius: '8px',
                          padding: '12px',
                          textAlign: 'center'
                        }}>
                          <div style={{ 
                            fontSize: '20px', 
                            fontWeight: '700', 
                            color: '#d97706',
                            marginBottom: '4px'
                          }}>
                            {validation.invalidCount}
                          </div>
                          <div style={{ fontSize: '12px', color: '#92400e', fontWeight: '500' }}>
                            Issues
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Issues */}
                    {validation.issues && validation.issues.length > 0 && (
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#dc2626',
                          marginBottom: '8px'
                        }}>
                          🚨 Issues Found:
                        </div>
                        <ul style={{
                          margin: 0,
                          paddingLeft: '16px',
                          fontSize: '13px',
                          color: '#7f1d1d'
                        }}>
                          {validation.issues.map((issue: string, idx: number) => (
                            <li key={idx} style={{ marginBottom: '4px' }}>
                              {issue}
                            </li>
                          ))}
                        </ul>
                        
                        {/* Special Phone Field Notice */}
                        {isPhone && (
                          <div style={{
                            background: '#ede9fe',
                            border: '1px solid #c4b5fd',
                            borderRadius: '6px',
                            padding: '8px',
                            marginTop: '8px'
                          }}>
                            <div style={{ fontSize: '12px', color: '#7c3aed', fontWeight: '600' }}>
                              📞 Phone Validation Rules:
                            </div>
                            <div style={{ fontSize: '11px', color: '#6d28d9', marginTop: '4px' }}>
                              • Only digits, spaces, and + (at start) allowed<br/>
                              • No special characters: -()+_<br/>
                              • Length: 10-15 digits
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Samples */}
                    {validation.samples && (
                      <div>
                        {validation.samples.valid.length > 0 && (
                          <div style={{ marginBottom: '12px' }}>
                            <div style={{
                              fontSize: '12px',
                              fontWeight: '600',
                              color: '#16a34a',
                              marginBottom: '4px'
                            }}>
                              ✅ Valid Examples:
                            </div>
                            <div style={{
                              background: '#f0fdf4',
                              border: '1px solid #bbf7d0',
                              borderRadius: '6px',
                              padding: '8px',
                              fontSize: '11px',
                              fontFamily: 'monospace',
                              color: '#166534'
                            }}>
                              {validation.samples.valid.slice(0, 3).join(', ')}
                            </div>
                          </div>
                        )}
                        
                        {validation.samples.invalid.length > 0 && (
                          <div>
                            <div style={{
                              fontSize: '12px',
                              fontWeight: '600',
                              color: '#dc2626',
                              marginBottom: '4px'
                            }}>
                              ❌ Issue Examples:
                            </div>
                            <div style={{
                              background: '#fef2f2',
                              border: '1px solid #fecaca',
                              borderRadius: '6px',
                              padding: '8px',
                              fontSize: '11px',
                              fontFamily: 'monospace',
                              color: '#7f1d1d'
                            }}>
                              {validation.samples.invalid.slice(0, 3).join(', ')}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );// Validation results
       return (
         <StepContent>
           <div style={{ textAlign: 'center', marginBottom: '24px' }}>
             <h3>Data Validation Results 🔍</h3>
             <p style={{ color: '#666' }}>
               We've analyzed your data to identify any potential issues before import.
             </p>
           </div>

           {validationResult && (
             <ValidationResults>
               <div className="validation-summary">
                 <h4>Summary</h4>
                 <p><strong>Total Rows:</strong> {validationResult.totalRows}</p>
                 <p><strong>Sample Analyzed:</strong> {validationResult.sampleSize}</p>
                 <p><strong>Overall Status:</strong> 
                   <span style={{ 
                     color: validationResult.canProceed ? '#52c41a' : '#ff4d4f',
                     fontWeight: 'bold',
                     marginLeft: '8px'
                   }}>
                     {validationResult.canProceed ? 'Ready to Import' : 'Issues Found'}
                   </span>
                 </p>
               </div>

               {Object.entries(validationResult.fieldResults).map(([field, validation]: [string, any]) => (
                 <div 
                   key={field} 
                   className={`field-validation ${validation.invalidCount > 0 ? 'has-issues' : 'valid'}`}
                 >
                   <h4>{formatFieldNameForDisplay(field)}</h4>
                   <div className="validation-stats">
                     <span className="valid">✓ {validation.validCount} valid</span>
                     {validation.invalidCount > 0 && (
                       <span className="invalid">✗ {validation.invalidCount} issues</span>
                     )}
                   </div>
                   
                   {validation.issues && validation.issues.length > 0 && (
                     <div className="recommendations">
                       <h5>Issues Found:</h5>
                       <ul>
                         {validation.issues.map((issue: string, idx: number) => (
                           <li key={idx}>{issue}</li>
                         ))}
                       </ul>
                     </div>
                   )}

                   {validation.samples && (
                     <div className="samples">
                       {validation.samples.valid.length > 0 && (
                         <div>
                           <div className="sample-label">✓ Valid Examples:</div>
                           <div className="sample-values">
                             {validation.samples.valid.slice(0, 3).join(', ')}
                           </div>
                         </div>
                       )}
                       {validation.samples.invalid.length > 0 && (
                         <div style={{ marginTop: '8px' }}>
                           <div className="sample-label">✗ Issue Examples:</div>
                           <div className="sample-values">
                             {validation.samples.invalid.slice(0, 3).join(', ')}
                           </div>
                         </div>
                       )}
                     </div>
                   )}
                 </div>
               ))}

               {!validationResult.canProceed && (
                 <div style={{ 
                   background: '#fff2f0', 
                   border: '1px solid #ffccc7', 
                   borderRadius: '8px', 
                   padding: '16px',
                   marginTop: '16px'
                 }}>
                   <h4 style={{ color: '#ff4d4f', marginBottom: '8px' }}>⚠️ Critical Issues Found</h4>
                   <p style={{ color: '#666', margin: 0 }}>
                     Your data has critical issues that may prevent successful import. 
                     You can either go back to fix the field mappings or proceed anyway (only valid rows will be imported).
                   </p>
                 </div>
               )}
             </ValidationResults>
           )}
         </StepContent>
       );
     
     case 3: // Success
  return (
    <StepContent>
      <CompletionContent>
        <FaCheckCircle className="success-icon" />
        <h3>CSV Upload Complete! 📧</h3>
        <p style={{ marginBottom: '24px' }}>
          Your CSV file has been submitted for processing.
          <br/>
          <strong>Check your email</strong> for detailed import results including:
        </p>
        
        <div style={{ textAlign: 'left', margin: '0 auto', maxWidth: '300px' }}>
          <ul>
            <li>✅ Successfully imported leads</li>
            <li>❌ Failed/skipped leads</li>
            <li>🔄 Duplicate handling results</li>
            <li>📋 Detailed error report</li>
          </ul>
        </div>
        
        <button 
          type="button"
          onClick={() => {
            setVisible(false);
            window.location.reload();
          }}
          style={{
            background: '#1890ff',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '6px',
            fontSize: '16px',
            cursor: 'pointer',
            marginTop: '20px'
          }}
        >
          Close & Refresh
        </button>
      </CompletionContent>
    </StepContent>
  );
     
     default:
       return null;
   }
 };

 // Following the exact same component pattern as existing
 return (
   <>
     {/* Dropdown item - MAINTAINING EXACT SAME PATTERN */}
     <li
       className="dropdown-item"
       onClick={() => setVisible(true)}
       style={{ border: "none !important" }}
     >
       <span role="img" aria-label="upload" style={{ marginRight: "8px" }}>
         📤
       </span>
       CSV bulk upload
     </li>

{/* Main popup with two-column layout */}
    <PopupComponent
        isOpen={visible}
        onClose={handleClosePopup}
        title="CSV Lead Import"
        size="lg"
      >
        <div style={{ 
          height: 'calc(80vh - 120px)', 
          display: 'flex', 
          overflow: 'hidden' 
        }}>
          {/* Left sidebar with modern step design */}
          <div style={{
            width: '280px',
            borderRight: '1px solid #e2e8f0',
            padding: '32px 24px',
            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative'
          }}>
            {/* Header */}
            <div style={{ marginBottom: '8px', textAlign: 'center' }}>
              <h3 style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: '700',
                color: '#1e293b',
                letterSpacing: '-0.025em'
              }}>
                CSV Import Process
              </h3>
             
            </div>

            {/* Centered Dotted Progress Line */}
            <div 
              style={{
                position: 'absolute',
                left: '50%',
                top: '120px',
                bottom: '120px',
                width: '3px',
                background: `repeating-linear-gradient(
                  to bottom,
                  #11998e 0px,
                  #11998e 8px,
                  transparent 8px,
                  transparent 16px
                )`,
                transform: 'translateX(-50%)',
                zIndex: 1,
                opacity: 0.6
              }} 
            />

            {/* Steps */}
            {[
              { number: 1, title: "Upload File", description: "Select your CSV file", icon: Upload },
              { number: 2, title: "Map Fields", description: "Match CSV columns", icon: ArrowRightLeft },
              { number: 3, title: "Validate Data", description: "Review & verify", icon: Shield },
              { number: 4, title: "Complete", description: "Import finished", icon: Trophy }
            ].map((step, index) => {
              const isActive = currentStep === index;
              const isCompleted = currentStep > index;
              const isClickable = currentStep > index;
              const StepIcon = step.icon;
              
              return (
                <div 
                  key={step.number}
                  style={{
                    position: 'relative',
                    zIndex: 2,
                    cursor: isClickable ? 'pointer' : 'default',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    marginBottom: index < 3 ? '24px' : '0'
                  }}
                  onClick={isClickable ? () => setCurrentStep(index) : undefined}
                >
                  <div 
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '16px',
                      background: isActive 
                        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
                        : isCompleted 
                        ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
                        : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                      borderRadius: '12px',
                      border: isActive 
                        ? '2px solid #667eea' 
                        : isCompleted 
                        ? '2px solid #11998e'
                        : '2px solid #e2e8f0',
                      boxShadow: isActive || isCompleted
                        ? '0 8px 25px rgba(0, 0, 0, 0.15)'
                        : '0 2px 8px rgba(0, 0, 0, 0.05)',
                      transform: isActive ? 'translateY(-2px) scale(1.02)' : 'translateY(0) scale(1)',
                    }}
                  >
                    {/* Icon Container */}
                    <div 
                      style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '12px',
                        background: isActive || isCompleted ? 'rgba(255, 255, 255, 0.2)' : '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '16px',
                        border: isActive || isCompleted ? '2px solid rgba(255, 255, 255, 0.3)' : '2px solid #e2e8f0',
                        backdropFilter: 'blur(10px)',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {isCompleted ? (
                        <Check 
                          size={24} 
                          color="white"
                          strokeWidth={3}
                        />
                      ) : (
                        <StepIcon 
                          size={24} 
                          color={isActive ? 'white' : isCompleted ? 'white' : '#64748b'}
                          strokeWidth={2}
                        />
                      )}
                    </div>

                    {/* Step Content */}
                    <div style={{ flex: 1 }}>
                      <div 
                        style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          color: isActive || isCompleted ? 'white' : '#334155',
                          marginBottom: '4px',
                          letterSpacing: '-0.025em'
                        }}
                      >
                        {step.title}
                      </div>
                      <div 
                        style={{
                          fontSize: '13px',
                          color: isActive || isCompleted ? 'rgba(255, 255, 255, 0.8)' : '#64748b',
                          fontWeight: '400',
                          lineHeight: '1.4'
                        }}
                      >
                        {step.description}
                      </div>
                    </div>

                    {/* Step Number Badge */}
                    <div 
                      style={{
                        position: 'absolute',
                        top: '-8px',
                        right: '-8px',
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        background: isCompleted 
                          ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
                          : isActive 
                          ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                          : 'linear-gradient(135deg, #cbd5e1 0%, #94a3b8 100%)',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: '700',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '2px solid white',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                      }}
                    >
                      {isCompleted ? <Check size={12} strokeWidth={3} /> : step.number}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Progress Footer */}
            <div style={{
              marginTop: 'auto',
              padding: '16px',
              background: 'rgba(255, 255, 255, 0.6)',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              textAlign: 'center'
            }}>
              <div style={{
                fontSize: '12px',
                color: '#64748b',
                fontWeight: '500'
              }}>
                Step {currentStep + 1} of 4
              </div>
              <div style={{
                width: '100%',
                height: '4px',
                background: '#e2e8f0',
                borderRadius: '2px',
                marginTop: '8px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${((currentStep + 1) / 4) * 100}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, #11998e 0%, #38ef7d 100%)',
                  borderRadius: '2px',
                  transition: 'width 0.3s ease'
                }} />
              </div>
            </div>
          </div>

          {/* Right content area */}
          <div style={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            {/* Error display */}
            {error && (
              <div style={{ 
                background: '#fff2f0', 
                border: '1px solid #ffccc7', 
                borderRadius: '8px', 
                padding: '12px 16px', 
                margin: '16px 16px 0 16px',
                display: 'flex',
                alignItems: 'center'
              }}>
                <span style={{ color: '#ff4d4f', marginRight: '8px' }}>⚠️</span>
                <span style={{ color: '#ff4d4f' }}>{error}</span>
              </div>
            )}
            
            {/* Main content area with more space */}
            <div style={{ 
              overflowY: 'auto', 
              flex: '1 1 auto', 
              padding: '16px 24px'
            }}>
              {renderStepContent()}
            </div>
            
            {/* Upload progress */}
            {isUploading && (
              <div style={{ 
                padding: '16px 24px', 
                borderTop: '1px solid #f0f0f0',
                background: '#fafafa'
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  gap: '12px'
                }}>
                  <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
                  <span>Importing leads... Please wait</span>
                </div>
              </div>
            )}
            
            {/* Action buttons */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '16px 24px',
              borderTop: '1px solid #f0f0f0',
              background: 'white'
            }}>
              <div>
                {currentStep > 0 && currentStep < 3 && (
                  <button 
                    onClick={handlePrevious} 
                    disabled={isUploading}
                    style={{
                      background: '#f5f5f5',
                      border: '1px solid #d9d9d9',
                      color: '#666',
                      padding: '10px 20px',
                      borderRadius: '6px',
                      cursor: isUploading ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    Previous
                  </button>
                )}
              </div>
              
              <div>
                {currentStep < 3 ? (
                  <button
                    onClick={handleNext}
                  disabled={isUploading || isReadingFile || !canProceedToStep(currentStep + 1)}
                    style={{
                      background: isUploading || isReadingFile || !canProceedToStep(currentStep + 1) ? '#d9d9d9' : '#1890ff',
                      color: 'white',
                      border: 'none',
                      padding: '10px 20px',
                      borderRadius: '6px',
                     cursor: isUploading || isReadingFile || !canProceedToStep(currentStep + 1) ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                {(isUploading || isReadingFile) && <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />}
{isReadingFile ? 'Reading File...' : currentStep === 2 ? 'Import Leads' : 'Next'}
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </PopupComponent>

     {/* Confirmation Modal following EXACT existing pattern */}
   <GenericConfirmationModal
  onConfirmation={() => {
    setFormCloseModel(false);
    setVisible(false);
  }}
  showModal={formCloseModel}
  toggleModal={() => setFormCloseModel(false)}
  title="Confirm Close"
  message="Are you sure you want to close? All progress will be lost."
  isWarning={true}
  confirmText="Yes, Close"
  cancelText="Cancel"
/>

     {/* Pass the actual selectedList prop to Google Sheets and Contacts components */}
     {/* These will continue to use their own drawer implementations */}
     <UploadSheetFile selectedList={selectedList} />
     <ImportGoogleContacts selectedList={selectedList} />
   </>
 );
};

export default UploadCsvFile;